import React, { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../assets/languages/configs';

import CenterLoading from '../CenterLoading/CenterLoading';
import usePurchaseTicket from '../../hooks/usePurchaseTicket';
import { APPLE_MERCHANT_IDENTIFIER, DEV_ENV_FLAG, PAYMENT_TYPE, SECRET_MANAGER_NAME } from '../../utils/Constants';
import { calcTotalPrice, hasSelectedNumber, showNotificationDialog } from '../../utils';
import './style.css';
import { SelectNumber, TicketInfo } from 'types';

interface ApplePaymentProps {
    ticketInfo: TicketInfo;
    selectNumber: SelectNumber | null;
    isConfirmChecked?: boolean;
    reservationCombo?: any;
    userForm?: any;
    anonymousFormAnswers?: any;
    onClick?: () => void;
    email?: string | null;
}

const ApplePayment = memo(
    ({
        ticketInfo,
        selectNumber,
        isConfirmChecked = true,
        reservationCombo,
        userForm,
        anonymousFormAnswers,
        onClick = () => {},
        email,
    }: ApplePaymentProps) => {
        const ApplePaySession = window.ApplePaySession;
        const ref = useRef<HTMLElement | null>(null);
        const {
            isLoading,
            purchaseWithCard,
            showOfflineError,
            hasEmailInUserInfo,
            WarningConfirmModal,
            EmailConfirmModal,
            applePayPaymentSession,
        } = usePurchaseTicket();
        const { salesTicketId } = ticketInfo ?? {};
        const { t } = useTranslation();

        const totalPrice = calcTotalPrice(selectNumber, {
            adult: ticketInfo?.priceAdult ?? 0,
            custom1: ticketInfo?.priceCustom1 ?? 0,
            custom2: ticketInfo?.priceCustom2 ?? 0,
            custom3: ticketInfo?.priceCustom3 ?? 0,
        });

        useEffect(() => {
            if (!ApplePaySession) {
                return;
            }
            // アプリ名をラベルとする。
            const configData = JSON.parse(localStorage.getItem('APP_CONFIG') ?? '{}');
            const appName = configData?.appName ?? 'QUICK TRIP';

            const paymentRequest = {
                countryCode: 'JP',
                currencyCode: 'JPY',
                total: {
                    label: appName,
                    amount: String(totalPrice),
                },
                supportedNetworks: ['amex', 'visa', 'masterCard', 'jcb', 'discover'],
                merchantCapabilities: ['supports3DS'],
            };

            const handleApplePayClick = async () => {
                if (!navigator.onLine) {
                    showOfflineError();
                    return;
                }
                if (!hasSelectedNumber(selectNumber)) {
                    showNotificationDialog(
                        'error',
                        'selectNumberError',
                        t('notification.title'),
                        t('validate.minimum-one-person'),
                    );
                    return;
                }
                if (isConfirmChecked === false) {
                    onClick();
                    return;
                }
                if (!hasEmailInUserInfo()) {
                    return;
                }
                const applePaySession = new ApplePaySession(2, paymentRequest);
                applePaySession.onvalidatemerchant = async (event: any) => {
                    try {
                        const validationURL = event.validationURL;
                        // Apple Pay確認
                        const response = await applePayPaymentSession({
                            validationUrl: validationURL,
                            initiativeContext: window.location.origin,
                            displayName: 'QUICK TRIP',
                            merchantIdentifier: APPLE_MERCHANT_IDENTIFIER,
                            secretName: SECRET_MANAGER_NAME,
                        });
                        applePaySession.completeMerchantValidation(response);
                    } catch {
                        applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
                    }
                };
                applePaySession.onpaymentmethodselected = async () => {
                    applePaySession.completePaymentMethodSelection({
                        newTotal: {
                            label: appName,
                            amount: String(totalPrice),
                        },
                    });
                };
                applePaySession.onpaymentauthorized = async (event: any) => {
                    const paymentToken = btoa(JSON.stringify(event.payment.token.paymentData));
                    if (paymentToken) {
                        const isPaymentSuccess = await purchaseWithCard({
                            salesTicketId: salesTicketId,
                            number: selectNumber,
                            cardToken: paymentToken,
                            paymentType: PAYMENT_TYPE.applePay,
                            reservationCombo,
                            userForm,
                            email: DEV_ENV_FLAG ? email : null,
                            anonymousFormAnswers,
                        });
                        if (isPaymentSuccess) {
                            applePaySession.completePayment(ApplePaySession.STATUS_SUCCESS);
                        } else {
                            applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
                        }
                    } else {
                        applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
                    }
                };
                applePaySession.oncancel = () => {
                    console.log('cancel');
                };
                applePaySession.begin();
            };

            const applePayButton = ref.current;
            if (!applePayButton) return;

            const clickHandler = () => {
                if (typeof handleApplePayClick === 'function') {
                    void handleApplePayClick();
                }
            };

            applePayButton.addEventListener('click', clickHandler);

            return () => {
                applePayButton.removeEventListener('click', clickHandler);
            };
        }, [
            selectNumber,
            totalPrice,
            isConfirmChecked,
            ApplePaySession,
            anonymousFormAnswers,
            salesTicketId,
            reservationCombo,
            userForm,
            hasEmailInUserInfo,
            onClick,
            purchaseWithCard,
            showOfflineError,
            applePayPaymentSession,
            t,
        ]);

        if (!ApplePaySession) {
            return null;
        }

        const buttonLocale = (() => {
            const { language } = i18n;
            const localeDict: Record<string, string> = {
                ja: 'ja-JP',
                en: 'en-US',
                vi: 'vi-VN',
                ko: 'ko-KR',
                th: 'th-TH',
                zh: 'zh-CN',
                'zh-TW': 'zh-TW',
            };
            return localeDict?.[language] || 'en-US';
        })();

        return (
            <>
                {isLoading && <CenterLoading overFooter />}
                <WarningConfirmModal />
                <EmailConfirmModal />
                {React.createElement('apple-pay-button', {
                    buttonstyle: 'black',
                    type: 'plain',
                    locale: buttonLocale,
                    ref: ref,
                })}
            </>
        );
    },
);

export default ApplePayment;
